// FieldVision - ZonePanel.tsx
// Zone-panel med zone-tabel, inputs, beregninger og download knap
// FIX v2: Zone-sektion kan foldes sammen, default LUKKET

import React, { useState, useMemo } from 'react';
import ZoneRow, { ZoneData, ZONE_COLORS } from './ZoneRow';
import DownloadModal from './DownloadModal';

interface ZonePanelProps {
  zones: ZoneData[];
  mapId: number;
  source?: string;      // Kortets kilde (drone/satellite)
  mapFormat?: string;   // Terminal format kortet er uploadet som (drone-kort)
  onZonesChange?: (zones: ZoneData[]) => void;
  onDelete?: () => void;
}

const ZonePanel: React.FC<ZonePanelProps> = ({
  zones: initialZones,
  mapId,
  source,
  mapFormat,
  onZonesChange,
  onDelete
}) => {
  const [unit, setUnit] = useState<'l/ha' | 'kg/ha'>('l/ha');
  const [zones, setZones] = useState<ZoneData[]>(initialZones);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // FIX: Synkroniser zones når initialZones ændres (fra parent)
  React.useEffect(() => {
    setZones(initialZones);
  }, [initialZones]);
  // FIX v2: Zone-sektion starter LUKKET
  const [isZonesExpanded, setIsZonesExpanded] = useState(false);

  // Filtrer zone 0 vaek og sorter efter zone nummer
  const activeZones = useMemo(() => {
    return zones
      .filter(z => z.zone !== 0)
      .sort((a, b) => a.zone - b.zone);
  }, [zones]);

  // Beregn totaler
  const totals = useMemo(() => {
    return activeZones.reduce(
      (acc, zone) => ({
        hectares: acc.hectares + zone.hectares,
        total: acc.total + (zone.rate * zone.hectares),
      }),
      { hectares: 0, total: 0 }
    );
  }, [activeZones]);

  const handleRateChange = (zoneNumber: number, newRate: number) => {
    const updatedZones = zones.map(z =>
      z.zone === zoneNumber
        ? { ...z, rate: newRate, rateInt: Math.round(newRate) }
        : z
    );
    setZones(updatedZones);
    if (onZonesChange) {
      onZonesChange(updatedZones);
    }
  };

  const handleUnitToggle = (newUnit: 'l/ha' | 'kg/ha') => {
    setUnit(newUnit);
    const updatedZones = zones.map(z => ({ ...z, unit: newUnit }));
    setZones(updatedZones);
    if (onZonesChange) {
      onZonesChange(updatedZones);
    }
  };

  const unitLabel = unit === 'l/ha' ? 'l' : 'kg';

  return (
    <div className="zone-panel">
      {/* FIX v2: Klikbar header der folder zone-sektionen ind/ud */}
      <button
        className="zone-panel-header"
        onClick={() => setIsZonesExpanded(!isZonesExpanded)}
      >
        <span className="header-title">Angiv mængde for hver zone</span>
        <div className="header-right">
          {!isZonesExpanded && (
            <span className="collapsed-summary">
              {activeZones.length} zoner | {Math.round(totals.total)} {unitLabel}
            </span>
          )}
          <span className={`fold-icon ${isZonesExpanded ? 'expanded' : ''}`}>
            &#9660;
          </span>
        </div>
      </button>

      {/* FIX v2: Zone-indhold kun synligt naar udfolddet */}
      {isZonesExpanded && (
        <>
          {/* Unit toggle */}
          <div className="unit-toggle-row">
            <div className="unit-toggle">
              <button
                className={`unit-btn ${unit === 'kg/ha' ? 'active' : ''}`}
                onClick={() => handleUnitToggle('kg/ha')}
              >
                kg/ha
              </button>
              <button
                className={`unit-btn ${unit === 'l/ha' ? 'active' : ''}`}
                onClick={() => handleUnitToggle('l/ha')}
              >
                l/ha
              </button>
            </div>
          </div>

          {/* Zone raekker */}
          <div className="zone-rows">
            {activeZones.map(zone => (
              <ZoneRow
                key={zone.zone}
                zone={zone}
                unit={unit}
                onRateChange={handleRateChange}
              />
            ))}
          </div>

          {/* Summary */}
          <div className="zone-summary">
            <div className="summary-row">
              <span className="summary-label">Total areal:</span>
              <span className="summary-value">{totals.hectares.toFixed(1)} ha</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Total mængde:</span>
              <span className="summary-value highlight">
                {Math.round(totals.total).toLocaleString('da-DK')} {unitLabel}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Action buttons - altid synlig */}
      <div className="zone-actions">
        <button
          className="btn-download"
          onClick={() => setShowDownloadModal(true)}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="download-icon">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Download til terminal
        </button>
      </div>

      {/* Download Modal */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        mapId={mapId}
        source={source}
        mapFormat={mapFormat}
        zones={zones}
        unit={unit}
        totals={totals}
      />

      <style>{`
        .zone-panel {
          padding: 12px;
          background: white;
        }

        .zone-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 8px 4px;
          background: none;
          border: none;
          cursor: pointer;
          transition: background 0.15s;
          border-radius: 6px;
        }

        .zone-panel-header:hover {
          background: #f9fafb;
        }

        .header-title {
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .collapsed-summary {
          font-size: 12px;
          color: #6b7280;
        }

        .fold-icon {
          font-size: 10px;
          color: #9ca3af;
          transition: transform 0.2s;
        }

        .fold-icon.expanded {
          transform: rotate(180deg);
        }

        .unit-toggle-row {
          display: flex;
          justify-content: flex-end;
          padding: 8px 0;
        }

        .unit-toggle {
          display: flex;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }

        .unit-btn {
          padding: 4px 10px;
          border: none;
          background: white;
          font-size: 11px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }

        .unit-btn:first-child {
          border-right: 1px solid #e5e7eb;
        }

        .unit-btn.active {
          background: #3c6c34;
          color: white;
        }

        .unit-btn:hover:not(.active) {
          background: #f9fafb;
        }

        .zone-rows {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-bottom: 8px;
        }

        .zone-summary {
          padding: 10px 0;
          padding-right: 16px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .summary-label {
          font-size: 12px;
          color: #6b7280;
        }

        .summary-value {
          font-size: 12px;
          font-weight: 500;
          color: #374151;
        }

        .summary-value.highlight {
          font-size: 13px;
          font-weight: 600;
          color: #3c6c34;
        }

        .zone-actions {
          margin-top: 10px;
        }

        .btn-download {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }

        .btn-download:hover {
          background: #15803d;
        }

        .download-icon {
          width: 16px;
          height: 16px;
        }
      `}</style>
    </div>
  );
};

export default ZonePanel;
